import { Text, mergeClasses } from "@fluentui/react-components";
import { ArrowUploadRegular } from "@fluentui/react-icons";
import {
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
  useId,
  useRef,
  useState,
} from "react";
import { useFileDropzoneStyles } from "./FileDropzone.styles";
import { IFileDropzoneProps } from "./FileDropzone.types";

export const FileDropzone = (props: IFileDropzoneProps): JSX.Element => {
  const {
    onFilesSelected,
    acceptedExtensions,
    multiple = true,
    disabled = false,
    disabledMessage,
    onValidationError,
  } = props;
  const styles = useFileDropzoneStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const helperId = useId();

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      processFiles(files);
      e.target.value = ""; // Reset so selecting the same file again triggers onChange
    }
  };

  const processFiles = (files: File[]): void => {
    const acceptedExtensionsLower = acceptedExtensions.map((ext) =>
      ext.toLowerCase().startsWith(".")
        ? ext.toLowerCase()
        : `.${ext.toLowerCase()}`,
    );

    const validFiles = files.filter((file) => {
      const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
      return acceptedExtensionsLower.includes(fileExtension);
    });

    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    } else if (files.length > 0) {
      onValidationError?.(
        `Please select files with the following extensions: ${acceptedExtensions.join(", ")}`,
      );
    }
  };

  const handleClick = (): void => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (!disabled && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleClick();
    }
  };

  const acceptAttribute = acceptedExtensions
    .map((ext) => (ext.startsWith(".") ? ext : `.${ext}`))
    .join(",");

  return (
    <>
      <div
        className={mergeClasses(
          styles.dropzone,
          isDragOver && styles.dropzoneActive,
          disabled && styles.dropzoneDisabled,
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-describedby={helperId}
        aria-disabled={disabled}
        onKeyDown={handleKeyDown}
      >
        <ArrowUploadRegular className={styles.icon} aria-hidden="true" />
        <Text size={300} weight="medium">
          {disabled && disabledMessage
            ? disabledMessage
            : "Drag and drop or click to upload"}
        </Text>
      </div>

      <Text id={helperId} size={200} className={styles.acceptedTypesLabel}>
        Accepted file types:{" "}
        {acceptedExtensions.map((ext) => ext.toUpperCase()).join(", ")}
      </Text>

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptAttribute}
        multiple={multiple}
        onChange={handleFileInputChange}
        className={styles.fileInput}
        disabled={disabled}
      />
    </>
  );
};
